import { get } from "svelte/store";
import { currentJob, money } from "./stores";
import {
    alcoholism, confidence, culture, drugs, energy, fitness, health,
    hygiene, iq, loneliness, optimism, smooth, stress,
    construction, welding, people_skills, pickpocket, driving, crime,
    dance_basic, dance_experience, dance_intermediate, dance_advance,
} from "./stats.js";
import { Job, JobCategories, JobCategoryTimes, makeStandardJob } from "./jobEngine.js";

export { Job, JobCategories, JobCategoryTimes };

export const unemployed = new Job({
    name: "unemployed",
    employer: "",
    description: "",
    category: JobCategories.Unemployed,
    background_url: "/img/livingroom_morning.png",
    yearly_salary: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    requirements: "",
    canApply: () => true,
    event: () => "Kicking the can..."
});

// --- Construction ---
// All seven jobs share the same daily wear-and-tear (fitness up, health/
// hygiene/energy/culture/loneliness down) and the same neutral/good/bad
// flavor-text shape; only the specific numbers, required skills, and
// promotion bar differ per position.

export const construction_trainee = makeStandardJob({
    name: "Construction Trainee",
    employer: "Brick & Mortar",
    description: "Difficult manual labor for the tough and tried.",
    category: JobCategories.Construction,
    background_url: "/img/job_construction.png",
    yearly_salary: [10500.0, 11000.0, 11500.0, 12900.0, 13400.0, 13800.0],
    // NOTE: the original hand-written requirements text claimed "Fitness > 50"
    // while canApply() actually only gated on fitness > 0.2 (trainee is meant
    // to be the low-bar entry position below construction_worker) — fixed
    // here since requirements text is now generated from the same data that
    // drives canApply(), so the two can no longer say different things.
    requirements: [
        { stat: health, op: '>', value: 0.5 },
        { stat: fitness, op: '>', value: 0.2 },
        { stat: alcoholism, op: '<', value: 0.9 },
        { stat: drugs, op: '<', value: 0.8 },
    ],
    dailyEffects: [
        { stat: construction, value: 0.005 },
        { stat: fitness, value: 0.025 },
        { stat: health, value: -0.01 },
        { stat: hygiene, value: -0.3 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: loneliness, value: -0.2 },
    ],
    promotion: { stats: [fitness, energy], threshold: 0.8 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the construction site. Hammering nails, mounting pipes, pouring concrete. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: energy, value: 0.15 }],
            text: "Had a blast at work today. The old Frank was cracking some jokes, and we were all laughing to tears for better part of the day. The time passed by very quickly.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.15 }, { stat: stress, value: 0.2 }],
            text: "The boss was in the bad mood today. He was around the whole time, inspecting our work, not letting anyone relax for a second. It was a fairly stressful day.",
        },
        onPromotionCheck: [{ stat: optimism, value: 0.3 }],
        promoted: {
            text: "The boss noticed my work and has taken a liking to me. He pulled me to the side today andtold me that I got promoted! Extra money will certainly be useful!",
        },
        notPromoted: {
            text: "The boss was really happy with our work today and even brought us some beer. I told me that he is particularly happy with my performance. I might have a chance for promotion if my performance proves to be reliable.",
        },
    },
});

export const construction_worker = makeStandardJob({
    name: "Construction Worker",
    employer: "Brick & Mortar",
    description: "Difficult manual labor for the tough and tried.",
    category: JobCategories.Construction,
    background_url: "/img/job_construction.png",
    yearly_salary: [14500.0, 15000.0, 15500.0, 15900.0, 16400.0, 16800.0],
    requirements: [
        { stat: health, op: '>', value: 0.5 },
        { stat: fitness, op: '>', value: 0.5 },
        { stat: alcoholism, op: '<', value: 0.9 },
        { stat: drugs, op: '<', value: 0.8 },
        { stat: construction, op: '>', value: 0.5 },
    ],
    dailyEffects: [
        { stat: construction, value: 0.005 },
        { stat: fitness, value: 0.025 },
        { stat: health, value: -0.01 },
        { stat: hygiene, value: -0.3 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: loneliness, value: -0.2 },
    ],
    promotion: { stats: [fitness, energy], threshold: 0.8 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the construction site. Hammering nails, mounting pipes, pouring concrete. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: energy, value: 0.15 }],
            text: "Had a blast at work today. The old Frank was cracking some jokes, and we were all laughing to tears for better part of the day. The time passed by very quickly.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.15 }, { stat: stress, value: 0.2 }],
            text: "The boss was in the bad mood today. He was around the whole time, inspecting our work, not letting anyone relax for a second. It was a fairly stressful day.",
        },
        onPromotionCheck: [{ stat: optimism, value: 0.3 }],
        promoted: {
            text: "The boss noticed my work and has taken a liking to me. He pulled me to the side today andtold me that I got promoted! Extra money will certainly be useful!",
        },
        notPromoted: {
            text: "The boss was really happy with our work today and even brought us some beer. I told me that he is particularly happy with my performance. I might have a chance for promotion if my performance proves to be reliable.",
        },
    },
});

export const construction_worker2 = makeStandardJob({
    name: "Construction Worker",
    employer: "Bird Town",
    description: "Difficult manual labor for the tough and tried.",
    category: JobCategories.Construction,
    background_url: "/img/job_construction.png",
    yearly_salary: [16500.0, 17000.0, 17500.0, 17900.0, 18400.0, 18600.0],
    requirements: [
        { stat: health, op: '>', value: 0.5 },
        { stat: fitness, op: '>', value: 0.5 },
        { stat: alcoholism, op: '<', value: 0.9 },
        { stat: drugs, op: '<', value: 0.8 },
        { stat: construction, op: '>', value: 0.5 },
    ],
    dailyEffects: [
        { stat: construction, value: 0.005 },
        { stat: fitness, value: 0.025 },
        { stat: health, value: -0.01 },
        { stat: hygiene, value: -0.3 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: loneliness, value: -0.2 },
    ],
    promotion: { stats: [fitness, energy, construction], threshold: 0.7 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the construction site. Hammering nails, mounting pipes, pouring concrete. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: energy, value: 0.15 }],
            text: "Had a blast at work today. The old Frank was cracking some jokes, and we were all laughing to tears for better part of the day. The time passed by very quickly.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.15 }, { stat: stress, value: 0.2 }],
            text: "The boss was in the bad mood today. He was around the whole time, inspecting our work, not letting anyone relax for a second. It was a fairly stressful day.",
        },
        onPromotionCheck: [{ stat: optimism, value: 0.3 }],
        promoted: {
            text: "The boss noticed my work and has taken a liking to me. He pulled me to the side today andtold me that I got promoted! Extra money will certainly be useful!",
        },
        notPromoted: {
            text: "The boss was really happy with our work today and even brought us some beer. I told me that he is particularly happy with my performance. I might have a chance for promotion if my performance proves to be reliable.",
        },
    },
});

export const construction_welder = makeStandardJob({
    name: "Welder",
    employer: "Turner Steel",
    description: "Tradesman job for anyone skilled with the welding equipment.",
    category: JobCategories.Construction,
    background_url: "/img/job_construction.png",
    yearly_salary: [16100.0, 16800.0, 17200.0, 17600.0, 18000.0, 18400.0],
    requirements: [
        { stat: health, op: '>', value: 0.5 },
        { stat: fitness, op: '>', value: 0.5 },
        { stat: alcoholism, op: '<', value: 0.9 },
        { stat: drugs, op: '<', value: 0.8 },
        { stat: construction, op: '>', value: 0.5 },
        { stat: welding, op: '>', value: 0.3 },
    ],
    dailyEffects: [
        { stat: construction, value: 0.005 },
        { stat: welding, value: 0.005 },
        { stat: fitness, value: 0.025 },
        { stat: health, value: -0.01 },
        { stat: hygiene, value: -0.3 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: loneliness, value: -0.2 },
    ],
    promotion: { stats: [fitness, energy, welding], threshold: 0.5 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the construction site. Welding pipes and sheet metal, sparks everywhere. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: energy, value: 0.15 }],
            text: "Had a blast at work today. The old Hank was cracking some jokes, and we were all laughing to tears for better part of the day. The time passed by very quickly.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.15 }, { stat: stress, value: 0.2 }],
            text: "The boss was in the bad mood today. He was around the whole time, inspecting our work, not letting anyone relax for a second. It was a fairly stressful day.",
        },
        onPromotionCheck: [{ stat: optimism, value: 0.3 }],
        promoted: {
            text: "The boss noticed my work and has taken a liking to me. He pulled me to the side today andtold me that I got promoted! Extra money will certainly be useful!",
        },
        notPromoted: {
            text: "The boss was really happy with our work today and even brought us some beer. I told me that he is particularly happy with my performance. I might have a chance for promotion if my performance proves to be reliable.",
        },
    },
});

export const construction_welder2 = makeStandardJob({
    name: "Welder",
    employer: "Big Spark",
    description: "Tradesman job for anyone skilled with the welding equipment.",
    category: JobCategories.Construction,
    background_url: "/img/job_construction.png",
    yearly_salary: [19100.0, 19800.0, 20200.0, 20600.0, 21000.0, 22100.0],
    requirements: [
        { stat: health, op: '>', value: 0.5 },
        { stat: fitness, op: '>', value: 0.5 },
        { stat: alcoholism, op: '<', value: 0.9 },
        { stat: drugs, op: '<', value: 0.8 },
        { stat: construction, op: '>', value: 0.6 },
        { stat: welding, op: '>', value: 0.7 },
    ],
    dailyEffects: [
        { stat: construction, value: 0.005 },
        { stat: welding, value: 0.005 },
        { stat: fitness, value: 0.025 },
        { stat: health, value: -0.01 },
        { stat: hygiene, value: -0.3 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: loneliness, value: -0.2 },
    ],
    promotion: { stats: [fitness, energy, welding], threshold: 0.7 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the construction site. Welding pipes and sheet metal, sparks everywhere. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: energy, value: 0.15 }],
            text: "Had a blast at work today. The old Bob was cracking some jokes, and we were all laughing to tears for better part of the day. The time passed by very quickly.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.15 }, { stat: stress, value: 0.2 }],
            text: "The boss was in the bad mood today. He was around the whole time, inspecting our work, not letting anyone relax for a second. It was a fairly stressful day.",
        },
        onPromotionCheck: [{ stat: optimism, value: 0.3 }],
        promoted: {
            text: "The boss noticed my work and has taken a liking to me. He pulled me to the side today andtold me that I got promoted! Extra money will certainly be useful!",
        },
        notPromoted: {
            text: "The boss was really happy with our work today and even brought us some beer. I told me that he is particularly happy with my performance. I might have a chance for promotion if my performance proves to be reliable.",
        },
    },
});

export const construction_welder3 = makeStandardJob({
    name: "Expert Welder",
    employer: "Big Spark",
    description: "Tradesman job for anyone skilled with the welding equipment.",
    category: JobCategories.Construction,
    background_url: "/img/job_construction.png",
    yearly_salary: [24100.0, 24800.0, 25200.0, 26600.0, 27000.0, 28100.0],
    requirements: [
        { stat: health, op: '>', value: 0.5 },
        { stat: fitness, op: '>', value: 0.5 },
        { stat: alcoholism, op: '<', value: 0.9 },
        { stat: drugs, op: '<', value: 0.8 },
        { stat: construction, op: '>', value: 0.5 },
        { stat: welding, op: '>', value: 0.91 },
    ],
    dailyEffects: [
        { stat: construction, value: 0.005 },
        { stat: welding, value: 0.005 },
        { stat: fitness, value: 0.025 },
        { stat: health, value: -0.01 },
        { stat: hygiene, value: -0.3 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: loneliness, value: -0.2 },
    ],
    promotion: { stats: [fitness, energy, welding], threshold: 0.8 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the construction site. Welding pipes and sheet metal, sparks everywhere. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: energy, value: 0.15 }],
            text: "Had a blast at work today. The old Bob was cracking some jokes, and we were all laughing to tears for better part of the day. The time passed by very quickly.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.15 }, { stat: stress, value: 0.2 }],
            text: "The boss was in the bad mood today. He was around the whole time, inspecting our work, not letting anyone relax for a second. It was a fairly stressful day.",
        },
        onPromotionCheck: [{ stat: optimism, value: 0.3 }],
        promoted: {
            text: "The boss noticed my work and has taken a liking to me. He pulled me to the side today andtold me that I got promoted! Extra money will certainly be useful!",
        },
        notPromoted: {
            text: "The boss was really happy with our work today and even brought us some beer. I told me that he is particularly happy with my performance. I might have a chance for promotion if my performance proves to be reliable.",
        },
    },
});

export const construction_welder4 = makeStandardJob({
    name: "Welding Trainer",
    employer: "Big Spark",
    description: "Teaching job for anyone skilled with the welding equipment and good with people.",
    category: JobCategories.Construction,
    background_url: "/img/job_construction.png",
    yearly_salary: [25100.0, 25800.0, 26200.0, 26800.0, 27500.0, 28200.0],
    requirements: [
        { stat: health, op: '>', value: 0.5 },
        { stat: fitness, op: '>', value: 0.5 },
        { stat: alcoholism, op: '<', value: 0.9 },
        { stat: drugs, op: '<', value: 0.8 },
        { stat: construction, op: '>', value: 0.5 },
        { stat: welding, op: '>', value: 0.95 },
        { stat: people_skills, op: '>', value: 0.7 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: welding, value: 0.005 },
        { stat: fitness, value: 0.025 },
        { stat: health, value: -0.01 },
        { stat: hygiene, value: -0.3 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: loneliness, value: -0.2 },
    ],
    promotion: { stats: [fitness, energy, welding], threshold: 0.8 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the class. Welding pipes and sheet metal, sparks everywhere. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: energy, value: 0.15 }],
            text: "Had a blast at work today. The old Bob was cracking some jokes, and we were all laughing to tears for better part of the day. The time passed by very quickly.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.15 }, { stat: stress, value: 0.2 }],
            text: "The class terrible today. Some showed up drunk and the other could not focus. One fella got burned, real bad. It was a fairly stressful day.",
        },
        onPromotionCheck: [{ stat: optimism, value: 0.3 }],
        promoted: {
            text: "The boss noticed my work and has taken a liking to me. He pulled me to the side today andtold me that I got promoted! Extra money will certainly be useful!",
        },
        notPromoted: {
            text: "The class successfully finished the welding course. They told me that they really enjoyed my teaching and that they learned a lot. The boss took notice and might consider promoting me if my performance proves to be reliable.",
        },
    },
});

// --- Hustle / crime ---
// Unlike construction, the >0.9 branch's optimism swing lives inside
// promoted/notPromoted (not shared), and money is adjusted on every branch
// except neutral — each job's exact money shape (fixed vs. salary-based,
// which branches touch it) differs and is preserved as found.

export const hustle_pickpocket = makeStandardJob({
    name: "Pickpocket",
    employer: "",
    description: "The smallest fish in the world of crime. You gotta start somewhere...",
    category: JobCategories.Hustle,
    background_url: "/img/street.png",
    yearly_salary: [10500.0, 12000.0, 13500.0, 14900.0, 15400.0, 16800.0],
    requirements: [],
    dailyEffects: [
        { stat: crime, value: 0.005 },
        { stat: people_skills, value: 0.005 },
        { stat: pickpocket, value: 0.005 },
        { stat: fitness, value: 0.01 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [people_skills, confidence, pickpocket], threshold: 0.4 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day on the streets. I successfully picked a couple of pockets. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }],
            money: () => get(currentJob).salary() / 2,
            text: "There was some event going on in the city. A bunch of tourists were around. Easy pray for pickpocket like me. Easy!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            money: -5,
            text: "Terrible situation today. Not many people around and some guy noticed me picking his pocket and started chasing me for a couple of blocks. Luckily I was able to outrun him.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: 50,
            text: "I am getting really good at this! Today I hit a jackpot with some business man and got notonly his walled but a golden watch too! The pawnshop gave me 50 for it! From now on I will starttargeting a bit a slightly higher caliber pray.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }],
            money: () => -get(money) * 0.75,
            text: "Damn it! I was trying to pickpocket a businessman and it turned out it was a damn pimp!I got my ass whooped and they took my money. Damn it! ",
        },
    },
});

export const hustle_smuggler = makeStandardJob({
    name: "Smuggler",
    employer: "",
    description: "Illegal transportation of objects and substances. Mostly cigarettes but you never know.",
    category: JobCategories.Hustle,
    background_url: "/img/street.png",
    yearly_salary: [19100.0, 19800.0, 20200.0, 20600.0, 21000.0, 22100.0],
    requirements: [{ stat: driving, op: '>', value: 0.9 }],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: crime, value: 0.005 },
        { stat: fitness, value: 0.01 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [people_skills, confidence, smooth, crime], threshold: 0.6 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Smooth ride today. Smuggled a bunch of cigarettes. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }],
            money: () => get(currentJob).salary() / 2,
            text: "There was some event going on in the city and I was a assigned a major package today. Cops were busy with all the bustle and tourists. Easy ridin!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            money: -10,
            text: "Terrible situation today. The cops were bored and started sniffing around. I had to bribe them with some cigarettes and money to get them off of me.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! Today I hit a jackpot with some exotic substancesand it all went down very smoothly. Doubled my daily wage today! Easy! I will start doingsome more high end smuggling from now on.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }],
            money: () => -get(money) * 0.75,
            text: "Damn it! I was trying to smuggle some drugs today and it turned out to be a ruse!I got my ass whooped and they took my money. Damn it! ",
        },
    },
});

export const hustle_burglar = makeStandardJob({
    name: "Burglar",
    employer: "",
    description: "Illegal breaking and entering, theft and robbery. Everything's up for grabs.",
    category: JobCategories.Hustle,
    background_url: "/img/street.png",
    yearly_salary: [16100.0, 16800.0, 17200.0, 17600.0, 18000.0, 18400.0],
    requirements: [{ stat: driving, op: '>', value: 0.9 }],
    dailyEffects: [
        { stat: crime, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: stress, value: 0.1 },
    ],
    promotion: { stats: [pickpocket, fitness, driving, iq], threshold: 0.7 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Smooth ride today. Broke into an appartment. Everything went as planned. We split the loot and went our ways.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }],
            money: () => get(currentJob).salary() / 2,
            text: "There was some event going on in the city and I was a assigned a fancy house. Cops were busy with all the bustle and tourists so we did our burglary with no hiccups. Easy!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            money: -10,
            text: "Terrible situation today. The cops were everywhere on the lookout. We had to bail early and barely made it out without getting caught.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! Today I hit a jackpot with some fancy mansionand it all went down very smoothly. Doubled my daily wage today! Easy! I will start doingsome more high homes from now on.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }],
            money: () => -get(money) * 0.75,
            text: "Damn it! I was trying to break in some mansion today and it turned out to be a ruse!I got my ass whooped and they took my money. Damn it! ",
        },
    },
});

export const hustle_dealer = makeStandardJob({
    name: "Drug Dealer",
    employer: "",
    description: "Wheelin' and dealin' some illegal substances. Everyone wants some. Only experienced criminals can do this.",
    category: JobCategories.Hustle,
    background_url: "/img/street.png",
    yearly_salary: [24100.0, 24800.0, 25200.0, 26600.0, 27000.0, 28100.0],
    requirements: [{ stat: crime, op: '>', value: 0.6 }],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: crime, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: stress, value: 0.1 },
    ],
    promotion: { stats: [smooth, crime, people_skills, confidence], threshold: 0.7 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day dealing to all the addicts. Nothing out of the ordinary.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }],
            money: () => get(currentJob).salary() / 2,
            text: "There was some event going on in the city and I had a major package to distribute. Cops were busy with all the bustle and tourists so I distributed the drugs with no issues. Easy!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            money: -10,
            text: "Terrible situation today. The cops were everywhere on the lookout. I had to get off the streets early and barely made it out without getting caught.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! Today I hit a jackpot with some high end customers and it all went down very smoothly. Doubled my daily wage today! Easy! I will start dealing to more high society people from now on.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }],
            money: () => -get(money) * 0.75,
            text: "Damn it! I was trying to deal in the club during some party and it turned out to be a ruse!I got my ass whooped and they took my money. Damn it! ",
        },
    },
});

export const hustle_pimp = makeStandardJob({
    name: "Pimp",
    employer: "",
    description: "Top cat of the crime world. Merciless and smooth. Everyone envies you.",
    category: JobCategories.Hustle,
    background_url: "/img/street.png",
    yearly_salary: [30500.0, 31200.0, 31700.0, 32200.0, 32700.0, 33200.0],
    requirements: [
        { stat: crime, op: '>', value: 0.7 },
        { stat: smooth, op: '>', value: 0.7 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.005 },
        { stat: smooth, value: 0.005 },
        { stat: crime, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.45 },
        { stat: culture, value: -0.01 },
        { stat: stress, value: 0.1 },
    ],
    promotion: { stats: [smooth, crime, people_skills, confidence], threshold: 0.75 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day taking the money from the girls.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }],
            money: () => get(currentJob).salary() / 2,
            text: "There was some event going on in the city and we had a lot of tourist customers. Girls did a great job today. Easy!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            money: () => -get(currentJob).salary() / 2,
            text: "Terrible situation today. The cops were everywhere on the lookout. I had to get off the streets early and some girls didn't make any mondey. Damn cops!",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! Today I hit a jackpot with some high end customers and it all went down very smoothly. Doubled my daily wage today! Easy! I will start finding some more businessman customers for the girls from now on.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }],
            money: () => -get(money) * 0.75,
            text: "Damn it! I was trying to collect the money from girls in the club today and it turned out to be a ruse! I got my ass whooped and they took my money. Damn it! ",
        },
    },
});

// --- Dance ---
// Same shape as hustle (branch-local optimism swing, no universal
// onPromotionCheck), but money only moves on good/promoted (never bad), and
// notPromoted always adds a small dance_experience consolation gain.
// dance_6/dance_7/dance_teacher_1 deliberately keep touching dance_intermediate
// in dailyEffects/promotion even though their own canApply()/requirements
// gate on dance_advance — a pre-existing inconsistency in the original
// content, preserved verbatim rather than "corrected" into a balance change.

export const dance_tap = makeStandardJob({
    name: "Tap Dancer",
    employer: "Tap Royal",
    description: "Tap dance your way to fame with the Tap Royal group!",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [14200.0, 14200.0, 14200.0, 14200.0, 14200.0, 14200.0],
    requirements: [
        { stat: dance_basic, op: '>', value: 0.68 },
        { stat: fitness, op: '>', value: 0.42 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.01 },
        { stat: smooth, value: 0.005 },
        { stat: dance_basic, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_basic, fitness, confidence], threshold: 0.55 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the rehearsal studio, perfecting our dance routine.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had a show at a small local club today. The crowd loved us!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "The show at the local theater we rehearsed for was a disaster! We did not prepare well enough and got paid barely to the half of negotiated price.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The show went really well and the crowd loved my solo dance.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the crowd with my solo performance but no one was impressed!",
        },
    },
});

export const dance_1 = makeStandardJob({
    name: "Dancer",
    employer: "Groove Gang",
    description: "Get down with us!",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [12900.0, 12900.0, 12900.0, 12900.0, 12900.0, 12900.0],
    requirements: [
        { stat: dance_basic, op: '>', value: 0.72 },
        { stat: dance_experience, op: '>', value: 0.1 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.01 },
        { stat: smooth, value: 0.005 },
        { stat: dance_basic, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_basic, dance_experience, confidence], threshold: 0.55 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the rehearsal studio, perfecting our dance routine.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had a show at a small local club today. The crowd loved us!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "The show at the local theater we rehearsed for was a disaster! We did not prepare well enough and got paid barely to the half of negotiated price.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The show went really well and the crowd loved my moves.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the crowd with my solo performance but no one was impressed!",
        },
    },
});

export const dance_2 = makeStandardJob({
    name: "Dancer",
    employer: "Funkers",
    description: "If you're a funky mofo, you at the right place son!",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [9500.0, 9500.0, 9500.0, 9500.0, 9500.0, 9500.0],
    requirements: [
        { stat: dance_basic, op: '>', value: 0.52 },
        { stat: fitness, op: '>', value: 0.2 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.01 },
        { stat: smooth, value: 0.005 },
        { stat: dance_basic, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_basic, fitness, confidence], threshold: 0.55 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the rehearsal studio, perfecting our dance routine.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had a show at a small local club today. The crowd loved us!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "The show at the local theater we rehearsed for was a disaster! We did not prepare well enough and got paid barely to the half of negotiated price.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The show went really well and the crowd loved my moves.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the crowd with my solo performance but no one was impressed!",
        },
    },
});

export const dance_3 = makeStandardJob({
    name: "Dancer",
    employer: "Earth, Wind & Fire",
    description: "World famous disco funk band looking for experienced dancers.",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [15500.0, 15500.0, 15500.0, 15500.0, 15500.0, 15500.0],
    requirements: [
        { stat: dance_intermediate, op: '>', value: 0.4 },
        { stat: dance_experience, op: '>', value: 0.2 },
        { stat: smooth, op: '>', value: 0.4 },
        { stat: confidence, op: '>', value: 0.7 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.015 },
        { stat: smooth, value: 0.01 },
        { stat: dance_intermediate, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_intermediate, fitness, confidence], threshold: 0.65 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the rehearsal studio, perfecting our dance routine.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had a medium size show at the town hall today. The crowd loved us!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "The show at the local theater was a disaster! We did not prepare well enough and got paid barely to the half of negotiated price.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The show went really well and the crowd loved us.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the crowd with my new moves but no one was impressed!",
        },
    },
});

export const dance_4 = makeStandardJob({
    name: "Dancer",
    employer: "Boogie Wonderland",
    description: "Disco club looking for dancers.",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [16700.0, 16700.0, 16700.0, 16700.0, 16700.0, 16700.0],
    requirements: [
        { stat: dance_intermediate, op: '>', value: 0.62 },
        { stat: dance_experience, op: '>', value: 0.32 },
        { stat: smooth, op: '>', value: 0.5 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.015 },
        { stat: smooth, value: 0.01 },
        { stat: dance_intermediate, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_intermediate, fitness, confidence], threshold: 0.65 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the club, pumping up the crowd.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had a huge birthday party at the club today. The crowd loved our special routine!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "Tonight was a disaster! We did not prepare well enough and the guests were laughing at our new dance routine.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The party went really well and the crowd loved us.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the crowd with my new moves but no one was impressed!",
        },
    },
});

export const dance_5 = makeStandardJob({
    name: "Dancer",
    employer: "Grease",
    description: "Newly opened disco club looking for dancers.",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [18700.0, 18700.0, 18700.0, 18700.0, 18700.0, 18700.0],
    requirements: [
        { stat: dance_intermediate, op: '>', value: 0.8 },
        { stat: dance_experience, op: '>', value: 0.42 },
        { stat: smooth, op: '>', value: 0.6 },
        { stat: confidence, op: '>', value: 0.7 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.015 },
        { stat: smooth, value: 0.01 },
        { stat: dance_intermediate, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_intermediate, fitness, confidence], threshold: 0.65 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the club, pumping up the crowd.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had a huge birthday party at the club today. The crowd loved our special routine!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "Tonight was a disaster! We did not prepare well enough and the guests were laughing at our new dance routine.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The party went really well and the crowd loved us.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the crowd with my new moves but no one was impressed!",
        },
    },
});

export const dance_6 = makeStandardJob({
    name: "Dancer",
    employer: "Boogie Geez",
    description: "Elite club looking for dancers.",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [26400.0, 26400.0, 26400.0, 26400.0, 26400.0, 26400.0],
    requirements: [
        { stat: dance_advance, op: '>', value: 0.9 },
        { stat: dance_experience, op: '>', value: 0.7 },
        { stat: smooth, op: '>', value: 0.75 },
    ],
    // dailyEffects/promotion reference dance_intermediate, not dance_advance
    // — pre-existing inconsistency vs. the requirements above, preserved as-is.
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.015 },
        { stat: smooth, value: 0.01 },
        { stat: dance_intermediate, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_intermediate, fitness, confidence], threshold: 0.65 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the club, pumping up the crowd.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had a huge birthday party at the club today. The crowd loved our special routine!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "Tonight was a disaster! We did not prepare well enough and the guests were laughing at our new dance routine.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The party went really well and the crowd loved us.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the crowd with my new moves but no one was impressed!",
        },
    },
});

export const dance_7 = makeStandardJob({
    name: "Dancer",
    employer: "Soul Train",
    description: "TV show looking for dancers.",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [23800.0, 23800.0, 23800.0, 23800.0, 23800.0, 23800.0],
    requirements: [
        { stat: dance_advance, op: '>', value: 0.73 },
        { stat: dance_experience, op: '>', value: 0.44 },
        { stat: confidence, op: '>', value: 0.7 },
    ],
    // dailyEffects touches dance_intermediate (not dance_advance) despite the
    // dance_advance requirement above; promotion correctly uses dance_advance.
    // Both preserved verbatim from the original, inconsistent as they are.
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.015 },
        { stat: smooth, value: 0.01 },
        { stat: dance_intermediate, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, dance_advance, dance_experience, confidence], threshold: 0.65 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the rehearsal studio, we are slowly getting better.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.1 }, { stat: dance_experience, value: 0.01 }],
            money: () => get(currentJob).salary() / 2,
            text: "We had the shooting of the tv show today. The director loved our special routine!",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "Tonight was a disaster! We did not prepare well enough and the shooting was super slow. The director was outraged.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The shooting went really well and the crowd was in a great mood.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the director with my new moves but no one was impressed!",
        },
    },
});

export const dance_teacher_1 = makeStandardJob({
    name: "Dancer",
    employer: "Funk Steps",
    description: "Dance school looking for a new teacher.",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [31200.0, 31200.0, 31200.0, 31200.0, 31200.0, 31200.0],
    requirements: [
        { stat: dance_advance, op: '>', value: 0.85 },
        { stat: fitness, op: '>', value: 0.65 },
        { stat: people_skills, op: '>', value: 0.64 },
        { stat: smooth, op: '>', value: 0.5 },
    ],
    // dailyEffects touches dance_intermediate (not dance_advance) despite the
    // dance_advance requirement above — preserved verbatim, as with dance_6/7.
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.015 },
        { stat: smooth, value: 0.01 },
        { stat: dance_intermediate, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [smooth, people_skills, dance_experience, confidence], threshold: 0.65 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the rehearsal studio, the students are slowly getting better.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "We had a dance off at the studio tonight and the student went wild! Everyone had a great time.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "Tonight was a slow session! The students were tired and not motivated. I couldn't get them to learn a single new move.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The students are having a great time and recommending ourschool to family and friends.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the students with my new moves today but no one was impressed.",
        },
    },
});

export const dance_teacher_2 = makeStandardJob({
    name: "Dancer",
    employer: "Kids on the Move",
    description: "Dance school for children looking for a new teacher.",
    category: JobCategories.Dance,
    background_url: "/img/stage.png",
    yearly_salary: [29200.0, 29200.0, 29200.0, 29200.0, 29200.0, 29200.0],
    requirements: [
        { stat: dance_intermediate, op: '>', value: 0.9 },
        { stat: fitness, op: '>', value: 0.5 },
        { stat: people_skills, op: '>', value: 0.74 },
    ],
    dailyEffects: [
        { stat: people_skills, value: 0.005 },
        { stat: confidence, value: 0.015 },
        { stat: smooth, value: 0.01 },
        { stat: dance_intermediate, value: 0.005 },
        { stat: dance_experience, value: 0.005 },
        { stat: fitness, value: 0.02 },
        { stat: energy, value: -0.5 },
        { stat: culture, value: 0.01 },
        { stat: stress, value: 0.05 },
    ],
    promotion: { stats: [fitness, people_skills, dance_experience, confidence], threshold: 0.65 },
    outcomes: {
        neutral: {
            effects: [{ stat: stress, value: 0.1 }],
            text: "Just a regular day at the dance school, the kids are having fun and slowly learning.",
        },
        good: {
            effects: [{ stat: optimism, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "We had a dance off at the dance school and the kids went wild! Everyone had a great time.",
        },
        bad: {
            effects: [{ stat: optimism, value: -0.1 }, { stat: stress, value: 0.2 }],
            text: "Tonight was a slow session! The kids were tired and cranky. I couldn't get them to learn a single new move.",
        },
        promoted: {
            effects: [{ stat: optimism, value: 0.3 }],
            money: () => get(currentJob).salary(),
            text: "I am getting really good at this! The kids are having a great time and even the parents arejoining in.",
        },
        notPromoted: {
            effects: [{ stat: optimism, value: -0.2 }, { stat: stress, value: 0.2 }, { stat: dance_experience, value: 0.01 }],
            text: "Damn it! I was trying to impress the kids with my new moves today but no one was impressed.",
        },
    },
});

export default {
    JobCategoryTimes,
    construction_trainee, construction_worker, construction_worker2, construction_welder, construction_welder2,
    construction_welder3, construction_welder4,
    hustle_pickpocket, hustle_smuggler, hustle_burglar, hustle_dealer, hustle_pimp,
    dance_tap, dance_1, dance_2, dance_3, dance_4, dance_5, dance_6, dance_7, dance_teacher_1, dance_teacher_2
}
